/**
 * Hotel Thank You Slice
 *
 * Manages hotel thank you (booking confirmation) state including:
 * - Thank you data loading states
 * - Booking confirmation data
 */
import { createSlice } from '@reduxjs/toolkit';
import type { HotelThankYouResponse } from '@contracts';
import { fetchHotelThankYouThunk } from '../thunks';

// ============================================
// State Types
// ============================================

export interface HotelThankYouState {
  // Loading state
  isLoading: boolean;
  // Error state
  error: string | null;
  // Thank you data
  data: HotelThankYouResponse | null;
}

// ============================================
// Initial State
// ============================================

export const initialHotelThankYouState: HotelThankYouState = {
  isLoading: false,
  error: null,
  data: null,
};

// ============================================
// Slice
// ============================================

const hotelThankYouSlice = createSlice({
  name: 'hotelThankYou',
  initialState: initialHotelThankYouState,
  reducers: {
    resetHotelThankYouState: () => initialHotelThankYouState,
    clearHotelThankYouError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Hotel Thank You
    builder
      .addCase(fetchHotelThankYouThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchHotelThankYouThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data;
      })
      .addCase(fetchHotelThankYouThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Failed to fetch hotel booking confirmation';
      });
  },
});

// ============================================
// Exports
// ============================================

export const { resetHotelThankYouState, clearHotelThankYouError } = hotelThankYouSlice.actions;
export const hotelThankYouReducer = hotelThankYouSlice.reducer;
